import { useState } from 'react';
import { MessageSquare, Plus, Edit2, Trash2, CheckCircle, XCircle, Copy } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { apiClient } from '@/lib/axios';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import toast from 'react-hot-toast';

interface WhatsAppTemplate {
  id: string;
  name: string;
  category: string;
  language: string;
  body: string;
  variables: string[];
  isApproved: boolean;
  wabaId?: string;
  createdAt: string;
}

interface TemplateForm {
  name: string;
  category: string;
  language: string;
  body: string;
}

const CATEGORY_OPTIONS = [
  { value: 'UTILITY', label: 'Utility' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'AUTHENTICATION', label: 'Authentication' },
];

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'en_IN', label: 'English (India)' },
];

function TemplateModal({
  template,
  onClose,
}: {
  template?: WhatsAppTemplate;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { register, handleSubmit, formState: { errors } } = useForm<TemplateForm>({
    defaultValues: template ? {
      name: template.name,
      category: template.category,
      language: template.language,
      body: template.body,
    } : { language: 'en', category: 'UTILITY' },
  });

  const mutation = useMutation({
    mutationFn: async (data: TemplateForm) => {
      if (template) {
        await apiClient.put(`/templates/${template.id}`, data);
      } else {
        await apiClient.post('/templates', data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast.success(template ? 'Template updated' : 'Template created');
      onClose();
    },
    onError: () => toast.error('Failed to save template'),
  });

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={template ? 'Edit Template' : 'New WhatsApp Template'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button form="template-form" type="submit" loading={mutation.isPending}>
            {template ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="template-form" onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          Templates must be approved by WhatsApp before use. Use {'{{1}}'}, {'{{2}}'} for variables.
        </div>
        <Input
          {...register('name', { required: 'Name is required' })}
          label="Template Name"
          error={errors.name?.message}
          placeholder="e.g. appointment_reminder"
        />
        <div className="grid grid-cols-2 gap-3">
          <Select
            {...register('category')}
            label="Category"
            options={CATEGORY_OPTIONS}
          />
          <Select
            {...register('language')}
            label="Language"
            options={LANGUAGE_OPTIONS}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Message Body <span className="text-red-500">*</span>
          </label>
          <textarea
            {...register('body', { required: 'Body is required' })}
            rows={5}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-cyan/30 focus:border-brand-cyan resize-none"
            placeholder="Hello {{1}}, your appointment is scheduled for {{2}}."
          />
          {errors.body && <p className="mt-1 text-xs text-red-500">{errors.body.message}</p>}
          <p className="mt-1 text-xs text-slate-400">
            Use {'{{1}}'}, {'{{2}}'} etc. for dynamic variables
          </p>
        </div>
      </form>
    </Modal>
  );
}

export function TemplatesPage() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState<WhatsAppTemplate | undefined>();

  const { data, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: { data: WhatsAppTemplate[] } }>('/templates');
      return data.data.data;
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast.success('Template deleted');
    },
    onError: () => toast.error('Failed to delete template'),
  });

  const syncTemplates = useMutation({
    mutationFn: () => apiClient.post('/templates/sync'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast.success('Templates synced with WhatsApp');
    },
    onError: () => toast.error('Sync failed'),
  });

  const copyBody = (body: string) => {
    navigator.clipboard.writeText(body);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare size={24} className="text-brand-navy" />
            WhatsApp Templates
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">{data?.length ?? 0} templates configured</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => syncTemplates.mutate()}
            loading={syncTemplates.isPending}
          >
            Sync with WhatsApp
          </Button>
          <Button size="sm" icon={<Plus size={14} />} onClick={() => setShowModal(true)}>
            New Template
          </Button>
        </div>
      </div>

      {/* Templates Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data?.map(template => (
            <div key={template.id} className="bg-white rounded-xl shadow-card border border-slate-100 p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-slate-900 font-mono text-sm">{template.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">
                      {template.category}
                    </span>
                    <span className="text-xs text-slate-400">{template.language}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {template.isApproved ? (
                    <div className="flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle size={14} />
                      <span>Approved</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-xs text-amber-600">
                      <XCircle size={14} />
                      <span>Pending</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-3 mb-3">
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{template.body}</p>
              </div>

              {template.variables.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-slate-500 mb-1">Variables:</p>
                  <div className="flex flex-wrap gap-1">
                    {template.variables.map((v, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 bg-brand-cyan/10 text-brand-cyan rounded font-mono">
                        {`{{${i + 1}}}`} = {v}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-1 pt-2 border-t border-slate-100">
                <button
                  onClick={() => copyBody(template.body)}
                  className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded transition-colors"
                >
                  <Copy size={12} /> Copy
                </button>
                <button
                  onClick={() => { setEditTemplate(template); setShowModal(true); }}
                  className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs text-slate-500 hover:text-brand-navy hover:bg-slate-50 rounded transition-colors"
                >
                  <Edit2 size={12} /> Edit
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Delete template "${template.name}"?`)) {
                      deleteTemplate.mutate(template.id);
                    }
                  }}
                  className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs text-slate-500 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}

          {!data?.length && (
            <div className="col-span-3 py-16 text-center text-slate-400">
              <MessageSquare size={40} className="mx-auto mb-3 opacity-30" />
              <p>No templates yet</p>
              <Button size="sm" className="mt-4" onClick={() => setShowModal(true)}>
                Create First Template
              </Button>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <TemplateModal
          template={editTemplate}
          onClose={() => { setShowModal(false); setEditTemplate(undefined); }}
        />
      )}
    </div>
  );
}
